import { ROI_CATEGORIES } from './roiCategories';

const DEFAULT_HOURLY_VALUE = {
  operations: 60,
  customer: 50,
  salesMarketing: 85,
  team: 70
};

const DEFAULT_HOURS = {
  operations: 5,
  customer: 4,
  salesMarketing: 6,
  team: 3
};

const KEYWORD_RULES = [
  { terms: ['invoice', 'billing', 'payment', 'quote', 'estimate'], category: 'operations', process: 'Invoice & payment processing', boost: 14 },
  { terms: ['schedule', 'dispatch', 'appointment', 'calendar'], category: 'operations', process: 'Scheduling coordination', boost: 12 },
  { terms: ['appointment', 'reminder', 'status update'], category: 'customer', process: 'Appointment confirmations or reminders', boost: 13 },
  { terms: ['question', 'support', 'ticket', 'chat'], category: 'customer', process: 'Responding to customer questions', boost: 10 },
  { terms: ['follow-up', 'follow up', 'summary', 'summaries'], category: 'customer', process: 'Creating client follow-up summaries', boost: 11 },
  { terms: ['content', 'blog', 'article'], category: 'salesMarketing', sub_category: 'content', process: 'Blog or article writing (drafting, editing, posting)', boost: 14 },
  { terms: ['social', 'caption', 'graphic'], category: 'salesMarketing', sub_category: 'content', process: 'Social media post creation (graphics, captions)', boost: 12 },
  { terms: ['lead', 'outreach', 'crm'], category: 'salesMarketing', sub_category: 'sales', process: 'Lead research or data cleanup', boost: 10 },
  { terms: ['report', 'dashboard', 'kpi', 'metrics'], category: 'team', process: 'Weekly dashboard updates', boost: 10 },
  { terms: ['meeting', 'notes', 'task'], category: 'team', process: 'Meeting note → task follow-up', boost: 9 },
  { terms: ['document', 'file', 'form', 'intake'], category: 'operations', process: 'Document filing or routing', boost: 9 },
  { terms: ['data cleanup', 'duplicate', 'quality'], category: 'team', process: 'Data cleanup or quality checks', boost: 8 }
];

const getSectionScore = (section = {}) => {
  if (typeof section.total === 'number') return section.total;
  return (section.ratings || []).reduce((sum, rating) => sum + (Number(rating) || 0), 0);
};

const getTextSignal = (auditData = {}) => [
  ...(auditData.top_frictions || []),
  ...Object.values(auditData.sections || {}).map(section => section?.notes || '')
].join(' ').toLowerCase();

const findProcess = (categoryId, processName, subCategoryId = null) => {
  const category = ROI_CATEGORIES[categoryId];
  const processes = subCategoryId
    ? category?.subCategories?.[subCategoryId]?.processes
    : category?.processes;
  return (processes || []).find(process => typeof process === 'object' && process.name === processName);
};

const addCandidate = (candidates, item) => {
  const key = `${item.category}:${item.sub_category || ''}:${item.name}`;
  const existing = candidates.get(key);
  if (!existing || item.score > existing.score) {
    candidates.set(key, item);
  }
};

const categoryDisplayName = (categoryId) => ROI_CATEGORIES[categoryId]?.label || categoryId;

export const getSmartRoiSuggestions = (auditData = {}, limit = 6) => {
  const sections = auditData.sections || {};
  const textSignal = getTextSignal(auditData);
  const existing = new Set((auditData.roi_items || []).map(item => String(item.process || '').toLowerCase()));
  const candidates = new Map();

  Object.keys(ROI_CATEGORIES).forEach(category => {
    const sectionScore = getSectionScore(sections[category]);
    const integration = auditData.tech_stack?.[category]?.integration_rating;
    const integrationBoost = integration && integration <= 2 ? 4 : 0;
    const baseScore = sectionScore + integrationBoost;
    const categoryData = ROI_CATEGORIES[category];

    const processGroups = categoryData.hasSubCategories
      ? Object.values(categoryData.subCategories).flatMap(sub => sub.processes.map(process => ({ process, sub_category: sub.id })))
      : (categoryData.processes || []).map(process => ({ process, sub_category: null }));

    processGroups.forEach(({ process, sub_category }) => {
      if (typeof process !== 'object') return;
      if (existing.has(process.name.toLowerCase())) return;
      const easyWinBoost = process.difficulty <= 2 ? 5 : process.difficulty === 3 ? 2 : 0;
      addCandidate(candidates, {
        category,
        sub_category,
        name: process.name,
        difficulty: process.difficulty || 3,
        suggested_hours_per_week: DEFAULT_HOURS[category],
        suggested_hourly_value: DEFAULT_HOURLY_VALUE[category],
        score: baseScore + easyWinBoost,
        reason: `Popular ${categoryDisplayName(category).toLowerCase()} task based on your ratings${integrationBoost ? ' and low tool integration' : ''}.`
      });
    });
  });

  KEYWORD_RULES.forEach(rule => {
    if (!rule.terms.some(term => textSignal.includes(term))) return;
    if (existing.has(rule.process.toLowerCase())) return;
    const process = findProcess(rule.category, rule.process, rule.sub_category);
    if (!process) return;
    addCandidate(candidates, {
      category: rule.category,
      sub_category: rule.sub_category || null,
      name: rule.process,
      difficulty: process.difficulty || 3,
      suggested_hours_per_week: DEFAULT_HOURS[rule.category],
      suggested_hourly_value: DEFAULT_HOURLY_VALUE[rule.category],
      score: getSectionScore(sections[rule.category]) + rule.boost,
      reason: `Suggested because you mentioned ${rule.terms[0]}-related friction.`
    });
  });

  return Array.from(candidates.values())
    .sort((a, b) => b.score - a.score || a.difficulty - b.difficulty)
    .slice(0, limit);
};
